import { DENIED, refreshConsent, isConsentSessionBlocked } from "./consent.js"

let config = {}
let consent = DENIED
let gaStarted = false
let gaReady = false
let gaSuspended = false
let lastGaPath = null
let reloading = false

const googleConsent = allowed => ({
  analytics_storage: allowed ? "granted" : "denied",
  ad_storage: "denied",
  ad_user_data: "denied",
  ad_personalization: "denied",
})

export function configureTracking(value) {
  config = value || {}
}

function analyticsAllowed() {
  return (
    consent.analytics &&
    !isConsentSessionBlocked() &&
    window.navigator.doNotTrack !== "1" &&
    window.doNotTrack !== "1"
  )
}

function pageData() {
  return {
    page_path: window.location.pathname,
    page_location: window.location.origin + window.location.pathname,
    page_referrer: "",
    // Do not attach medical article titles to automatic events.
    page_title: "Медицински център Тринити",
  }
}

function appendScript(id, src, onLoad) {
  const script = document.createElement("script")
  script.id = id
  script.async = true
  script.src = src
  script.referrerPolicy = "no-referrer"
  script.onload = () => {
    if (!reloading) onLoad()
  }
  document.head.appendChild(script)
}

function clearAnalyticsCookies() {
  const matches = /^(_ga(?:_|$)|_gid$|_gat(?:_|$))/
  const hosts = window.location.hostname.split(".")
  const domains = [
    "",
    ...hosts
      .map((_, i) => hosts.slice(i).join("."))
      .filter(host => host.includes(".")),
  ]
  const segments = window.location.pathname.split("/").filter(Boolean)
  const paths = [
    "/",
    ...segments.map((_, i) => "/" + segments.slice(0, i + 1).join("/")),
  ]
  document.cookie.split(";").forEach(cookie => {
    const name = cookie.split("=")[0].trim()
    if (!matches.test(name)) return
    domains.forEach(domain =>
      paths.forEach(path => {
        document.cookie = `${name}=; Max-Age=0; Path=${path};${
          domain ? ` Domain=${domain};` : ""
        } SameSite=Lax`
      })
    )
  })
}

function reloadOnce() {
  if (reloading) return
  reloading = true
  window.location.reload()
}

function startGoogle() {
  if (gaStarted || !/^G-[A-Z0-9]+$/.test(config.gaId || "")) return
  gaStarted = true
  window[`ga-disable-${config.gaId}`] = false
  window.dataLayer = window.dataLayer || []
  window.gtag = function () {
    window.dataLayer.push(arguments)
  }
  // Basic mode: this queue and the external loader exist only after permission.
  window.gtag("consent", "default", googleConsent(false))
  window.gtag("consent", "update", googleConsent(true))
  window.gtag("js", new Date())
  window.gtag("config", config.gaId, {
    send_page_view: false,
    allow_google_signals: false,
    allow_ad_personalization_signals: false,
    ...pageData(),
  })
  appendScript(
    "trinity-ga",
    `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(
      config.gaId
    )}`,
    () => {
      gaReady = true
      trackRoute()
    }
  )
}

export function applyTrackingConsent(next = DENIED) {
  if (typeof window === "undefined") return
  consent = next || DENIED
  const gaRevoked = !analyticsAllowed() && gaStarted
  const mapsRevoked =
    !consent.externalContent &&
    !!document.querySelector('script[src*="maps.googleapis.com/maps/api/js"]')

  if (!analyticsAllowed()) {
    lastGaPath = null
    if (config.gaId) window[`ga-disable-${config.gaId}`] = true
    if (gaStarted) {
      gaSuspended = true
      window.gtag("consent", "update", googleConsent(false))
    }
    try {
      clearAnalyticsCookies()
    } catch {
      // Cookie access can be blocked independently of consent storage.
      // Cleanup failure must not bypass the disabled state or break the UI.
    }
  }
  if (gaRevoked || mapsRevoked) {
    // Never reload into a known stale grant after storage failure. The shared
    // session latch keeps synchronization, route hooks and late loads denied.
    if (!isConsentSessionBlocked()) reloadOnce()
    return
  }
  if (process.env.NODE_ENV !== "production" || reloading) return
  if (analyticsAllowed()) {
    // A new explicit choice can recover after a failed save without a second
    // loader. Ordinary persisted withdrawal still uses a fresh document.
    if (gaSuspended) {
      gaSuspended = false
      window[`ga-disable-${config.gaId}`] = false
      window.gtag("consent", "update", googleConsent(true))
    }
    startGoogle()
  }
  sendPageViews()
}

function sendPageViews() {
  if (reloading) return
  if (
    analyticsAllowed() &&
    gaReady &&
    lastGaPath !== window.location.pathname
  ) {
    lastGaPath = window.location.pathname
    window.gtag("set", pageData())
    window.gtag("event", "page_view", { ...pageData(), send_to: config.gaId })
  }
}

export function beforeRouteUpdate({ location }) {
  if (typeof window === "undefined") return
  const { record } = refreshConsent()
  applyTrackingConsent(record || DENIED)
  // Keep GA automatic-event context sanitized during the route transition too.
  if (gaStarted && analyticsAllowed() && !reloading) {
    window.gtag("set", {
      ...pageData(),
      page_path: location.pathname,
      page_location: window.location.origin + location.pathname,
    })
  }
}

export function trackRoute() {
  if (typeof window === "undefined" || reloading) return
  // Revalidate at dispatch time, including async script loads and expired grants.
  const { record } = refreshConsent()
  applyTrackingConsent(record || DENIED)
}
